import React from 'react';
import { Card, Form, Input, Modal, Tag, message } from 'antd';
import { LoadingOutlined, ProfileOutlined, CheckCircleOutlined } from '@ant-design/icons';
import IntlMessages from '../../../../../util/IntlMessages';
import { useIntl } from 'react-intl';
import { useMutation, useQuery } from '@apollo/client';
import { GENERATE_AICS_CARD } from '../../../../../apollo/mutation/aics';
import { AICS_CURRENT_ACTIVITY, PRINT_AICS_CARD } from '../../../../../apollo/query/aics';
import { defaultCatchException, defaultMutationHandler } from '../../../../../apollo/callbacks';
import CardDescriptions, { CardDescriptionsItem } from '../../../../../components/Descriptions/CardDescription';
import { PrintButton } from '../../../../../components/FileButton';
import AicsMunicipalityInput from './aicsMunicipalityInput';

const AicsCard = ({ player, subEvent, mode = 'view' }) => {

    const intl = useIntl();

    const [generateAICSCard, { loading }] = useMutation(GENERATE_AICS_CARD);

    const { data: activityData } = useQuery(AICS_CURRENT_ACTIVITY, {
        fetchPolicy: 'cache-first',
    });

    const activity = activityData?.aicsCurrentActivity;

    const isEdit = mode === 'edit';

    const onClickGenerate = () => {
        if (!subEvent.aics_card_type) {
            message.error(intl.formatMessage({ id: 'common.aics_card_type_not_found' }));
            return;
        }

        Modal.confirm({
            title: intl.formatMessage({ id: 'common.confirm' }),
            content: intl.formatMessage({ id: 'common.generate_aics_confirmation' }),
            okText: intl.formatMessage({ id: 'common.yes' }),
            cancelText: intl.formatMessage({ id: 'common.cancel' }),
            onOk: () =>
                generateAICSCard({
                    variables: { id: player.id },
                })
                    .then(
                        defaultMutationHandler({
                            successCallback: () => {
                                message.success(intl.formatMessage({ id: 'common.operation_successful' }));
                            },
                            errorCallback: (e) => {
                                const raw = e?.length > 0 && e[0]?.message;
                                const rawMarker = 'server_error.aics_raw|';
                                if (raw && raw.startsWith(rawMarker)) {
                                    message.error(raw.substring(rawMarker.length), 8);
                                } else if (raw && intl.messages[raw]) {
                                    message.error(intl.formatMessage({ id: raw }), 8);
                                } else {
                                    defaultCatchException(e, intl);
                                }
                            },
                        }),
                    )
                    .catch((e) => defaultCatchException(e, intl)),
        });
    };

    const renderActions = () => {
        if (player.aics_card_number) {
            return [(
                <span key="aics-card-done" className="gx-text-success">
                    <CheckCircleOutlined className="gx-pr-1" />
                    <IntlMessages id="common.aics_card_number" />: <strong>{player.aics_card_number}</strong>
                </span>
            ), (
                <PrintButton
                    key="print"
                    query={{ query: PRINT_AICS_CARD, variables: { id: player.id } }}
                    context="printAICSCard"
                    title={intl.formatMessage({ id: 'common.print_aics_card' })}
                    filename="tessera-aics.pdf"
                />
            )];
        }

        return [(
            <a
                onClick={loading ? undefined : onClickGenerate}
                key="generate_aics"
                style={loading ? { pointerEvents: 'none', opacity: 0.6 } : undefined}
            >
                {loading ? <LoadingOutlined className="gx-pr-1" /> : <ProfileOutlined className="gx-pr-1" />}
                <IntlMessages id="common.generate" />
            </a>
        )];
    };

    return (
        <Card
            title={<IntlMessages id="common.aics_section_title" />}
            className="gx-card"
            actions={renderActions()}
        >
            <div className="gx-mb-2">
                <IntlMessages id="common.type" /> <Tag>{subEvent.aics_card_type || intl.formatMessage({ id: 'common.not_available' })}</Tag>
            </div>

            {activity && (
                <div className="gx-mb-3">
                    <IntlMessages id="common.aics_activity" />{' '}
                    <Tag color="blue">
                        {activity.aics_code} - {activity.name}
                        {activity.discipline ? ` (${activity.discipline})` : ''}
                    </Tag>
                </div>
            )}

            {isEdit ? (
                <>
                    <Form.Item
                        name="fiscal_code"
                        label={<IntlMessages id="common.fiscal_code" />}
                        normalize={(v) => (v || '').toUpperCase().trim()}
                        rules={[
                            {
                                pattern: /^[A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]$/,
                                message: intl.formatMessage({ id: 'common.fiscal_code_validation' }),
                            },
                        ]}
                    >
                        <Input maxLength={16} />
                    </Form.Item>

                    <Form.Item
                        name="birth_city"
                        label={<IntlMessages id="common.birth_city" />}
                    >
                        <AicsMunicipalityInput />
                    </Form.Item>

                    <Form.Item name="birth_province" hidden>
                        <Input />
                    </Form.Item>
                </>
            ) : (
                <CardDescriptions column={1} className="gx-mx-0">
                    {player.fiscal_code && (
                        <CardDescriptionsItem label={<IntlMessages id="common.fiscal_code" />}>
                            {player.fiscal_code}
                        </CardDescriptionsItem>
                    )}
                    {player.birth_city && (
                        <CardDescriptionsItem label={<IntlMessages id="common.birth_city" />}>
                            {player.birth_city}{player.birth_province ? ` (${player.birth_province})` : ''}
                        </CardDescriptionsItem>
                    )}
                    {player.aics_member_code && (
                        <CardDescriptionsItem label={<IntlMessages id="common.aics_member_code" />}>
                            <Tag color="green">{player.aics_member_code}</Tag>
                        </CardDescriptionsItem>
                    )}
                    {player.aics_card_number && (
                        <CardDescriptionsItem label={<IntlMessages id="common.aics_card_number" />}>
                            <Tag color="green">{player.aics_card_number}</Tag>
                        </CardDescriptionsItem>
                    )}
                </CardDescriptions>
            )}
        </Card>
    );
};

export default AicsCard;
